import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Users, Film, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { mockData } from '../data/mock';
import OptimizedImage from '../components/OptimizedImage';

const Services = () => {
  const { services } = mockData;

  return (
    <div className="bg-dark-bg">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/PC4.jpg"
            alt="Our Services"
            className="w-full h-full object-cover"
            priority={true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center pt-32 pb-20">
          <p className="text-gold text-sm tracking-widest uppercase mb-4">What We Offer</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-silver leading-tight">
            Comprehensive Event
            <span className="gold-gradient-text block mt-2">Solutions</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto mt-6">
            From conceptualization to execution, we deliver excellence across every aspect of event management.
          </p>
          {/* Golden Decorative Line */}
          <div className="flex justify-center mt-8">
            <div className="relative w-96 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent shadow-lg" style={{
              boxShadow: '0 0 15px rgba(255, 215, 0, 0.4), 0 0 30px rgba(255, 215, 0, 0.2)'
            }}>
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gold rounded-full shadow-lg" style={{
                boxShadow: '0 0 10px rgba(255, 215, 0, 0.6), 0 0 20px rgba(255, 215, 0, 0.3)'
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="space-y-32">
            {services.map((service, index) => {
              const IconComponent = service.icon === 'Sparkles' ? Sparkles : service.icon === 'Users' ? Users : Film;
              const isEven = index % 2 === 0;
              
              return (
                <div key={service.id} className={`grid lg:grid-cols-2 gap-16 items-center ${
                  !isEven ? 'lg:flex-row-reverse' : ''
                }`}>
                  {/* Image */}
                  <div className={`relative ${!isEven ? 'lg:order-2' : ''}`}>
                    <OptimizedImage
                      src={service.image}
                      alt={service.title}
                      className="rounded-sm w-full h-[500px] object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className={`absolute -bottom-8 ${isEven ? '-right-8' : '-left-8'} w-64 h-64 bg-gold/10 rounded-sm -z-10`}></div>
                    <div className="absolute top-8 left-8 bg-black/80 backdrop-blur-sm p-6 rounded-sm border border-gold/20">
                      <IconComponent className="text-gold" size={48} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`space-y-8 ${!isEven ? 'lg:order-1' : ''}`}>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-1 bg-gold"></div>
                        <span className="text-gold text-sm tracking-widest uppercase">Service {service.id}</span>
                      </div>
                      <Link to={`/services/${service.id === 1 ? 'events-exhibitions' : service.id === 2 ? 'talent-booking' : 'media-production'}`} className="group/title">
                        <h2 className="text-4xl md:text-5xl font-bold text-silver leading-tight hover:text-gold transition-colors cursor-pointer">
                          {service.title}
                        </h2>
                      </Link>
                      <p className="text-gray-300 text-lg leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    {/* Features List */}
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-silver">What We Offer:</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-3 group">
                            <CheckCircle2 className="text-gold flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" size={20} />
                            <span className="text-gray-300 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <Link to={`/services/${service.id === 1 ? 'events-exhibitions' : service.id === 2 ? 'talent-booking' : 'media-production'}`}>
                        <Button variant="outline" className="border-2 border-gold text-gold hover:bg-gold hover:text-black px-8 py-3 rounded-none transition-all duration-300 flex items-center gap-2">
                          Learn More
                          <ArrowRight size={18} />
                        </Button>
                      </Link>
                      <Link to="/contact">
                        <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-8 py-3 rounded-none transition-all duration-300 flex items-center gap-2">
                          Inquire
                          <ArrowRight size={18} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
            <p className="text-gold text-sm tracking-widest uppercase">Our Process</p>
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              How We Work
            </h2>
            <p className="text-gray-300 text-lg">
              A seamless, collaborative approach to bringing your vision to life.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Consultation",
                description: "We listen to your vision, understand your objectives, and assess your requirements."
              },
              {
                step: "02",
                title: "Planning",
                description: "Our team develops a comprehensive event plan with creative concepts and detailed logistics."
              },
              {
                step: "03",
                title: "Execution",
                description: "We bring your event to life with flawless coordination and attention to every detail."
              },
              {
                step: "04",
                title: "Follow-up",
                description: "Post-event analysis and support to ensure lasting success and satisfaction."
              }
            ].map((process, index) => (
              <Card key={index} className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group">
                <div className="p-8 space-y-4">
                  <div className="text-6xl font-bold gold-gradient-text opacity-20 group-hover:opacity-100 transition-opacity">
                    {process.step}
                  </div>
                  <h3 className="text-2xl font-bold text-silver group-hover:text-gold transition-colors">
                    {process.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">{process.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <Card className="bg-dark-grey border-gold/20 overflow-hidden">
            <div className="grid lg:grid-cols-2">
              <div className="p-12 lg:p-16 space-y-8 flex flex-col justify-center">
                <div className="space-y-4">
                  <p className="text-gold text-sm tracking-widest uppercase">Ready to Begin?</p>
                  <h2 className="text-4xl md:text-5xl font-bold text-silver leading-tight">
                    Let's Create Something
                    <span className="gold-gradient-text"> Remarkable</span>
                  </h2>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    Whether you're planning a corporate event, wedding, or brand activation, our team is ready 
                    to make your vision a reality.
                  </p>
                </div>
                <Link to="/contact">
                  <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-8 py-4 text-lg rounded-none transition-all duration-300 flex items-center gap-2">
                    Get Started Today
                    <ArrowRight size={20} />
                  </Button>
                </Link>
              </div>
              <div className="relative h-full min-h-[400px]">
                <OptimizedImage
                  src="https://images.unsplash.com/photo-1750735457984-fbc8cb61374a"
                  alt="Luxury Event"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-dark-grey via-dark-grey/20 to-transparent"></div>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Services;